import React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

// Enable custom parse format to handle date strings correctly in local timezone
dayjs.extend(customParseFormat);

interface DateInputProps {
  label?: string;
  value: string | null; // ISO format YYYY-MM-DD
  onChange: (value: string | null) => void;
  disabled?: boolean;
  min?: string; // ISO format YYYY-MM-DD
  max?: string; // ISO format YYYY-MM-DD
  helperText?: string;
}

/**
 * DateInput component that displays dates in MM/DD/YYYY format
 * using MUI DatePicker
 */
const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  min,
  max,
  helperText,
}) => {
  const handleChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      // Extract year, month, day directly to avoid timezone issues
      const year = newValue.year();
      const month = String(newValue.month() + 1).padStart(2, "0");
      const day = String(newValue.date()).padStart(2, "0");
      onChange(`${year}-${month}-${day}`);
    } else {
      onChange(null);
    }
  };

  // Parse the date string in local timezone to prevent timezone shifts
  // This ensures "2024-01-15" always displays as Jan 15, regardless of user's timezone
  const parseDateString = (
    dateStr: string | null | undefined,
  ): Dayjs | null => {
    if (!dateStr) return null;
    // CRITICAL: Manually construct the date to avoid ANY timezone interpretation
    // Split the ISO date and create a dayjs object at local midnight
    const [year, month, day] = dateStr.split("-").map(Number);
    // Create a date string that dayjs will interpret as local time (no 'Z' suffix)
    // Format: YYYY-MM-DDTHH:mm:ss (local time, not UTC)
    return dayjs(
      `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`,
    );
  };

  const dayjsValue = parseDateString(value);
  const minDate = parseDateString(min) || undefined;
  const maxDate = parseDateString(max) || undefined;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium mb-1 text-[#262626]">
          {label}
        </label>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjsValue}
          onChange={handleChange}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          format="MM/DD/YYYY"
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              helperText: helperText,
              placeholder: "MM/DD/YYYY",
              sx: {
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#D9D9D9",
                  },
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </div>
  );
};

export default DateInput;
